import Flex from '@/components/common/Flex';
import AiFormWarningAlert from '@/app/common/components/AiFormWarningAlert';
import AiNoActiveApiKeyAlert from '@/app/common/components/AiNoActiveApiKeyAlert';
import type { ChatMessage, ModalSize } from '@/app/KeywordTracker/types/aiModalTypes';
import SimpleBarReact from 'simplebar-react';
import { Card } from 'react-bootstrap';
import AiModalAccessAlerts from './AiModalAccessAlerts';
import AnalysisLoadingState from './AnalysisLoadingState';
import ChatItems from './chat/ChatItems';
import ChatPreload from './chat/ChatPreload';

interface AiModalContentProps {
  isMdUp: boolean;
  size: ModalSize;
  keyword?: string;
  messages: ChatMessage[];
  isFetchingHistoryList: boolean;
  isFetchingHistoryDetails: boolean;
  hasKeywordHistory: boolean;
  isHistoryListLoaded: boolean;
  isFetchingAnalysis: boolean;
  suppressAnalysisLoading: boolean;
  showInsufficientBalance?: boolean;
  showNoActiveApiKeyAlert?: boolean;
  onCloseNoActiveApiKeyAlert?: () => void;
  formWarningMessage?: string | null;
  onCloseFormWarning?: () => void;
  needsApiKeySetup?: boolean;
  canUseAi?: boolean;
}

const ChatScrollArea = ({
  isMdUp,
  size,
  messages,
  keyword,
  showInsufficientBalance,
  showNoActiveApiKeyAlert,
  onCloseNoActiveApiKeyAlert,
  formWarningMessage,
  onCloseFormWarning
}: {
  isMdUp: boolean;
  size: ModalSize;
  messages: ChatMessage[];
  keyword?: string;
  showInsufficientBalance?: boolean;
  showNoActiveApiKeyAlert?: boolean;
  onCloseNoActiveApiKeyAlert?: () => void;
  formWarningMessage?: string | null;
  onCloseFormWarning?: () => void;
}) => (
  <Card.Body as={Flex} className="p-0 flex-1 flex-column">
    {showInsufficientBalance ? (
      <AiModalAccessAlerts
        needsApiKeySetup={false}
        canUseAi
        showInsufficientBalance
      />
    ) : null}
    {showNoActiveApiKeyAlert ? (
      <div className="px-3 pt-3">
        <AiNoActiveApiKeyAlert
          show={showNoActiveApiKeyAlert}
          onClose={onCloseNoActiveApiKeyAlert ?? (() => undefined)}
        />
      </div>
    ) : null}
    {formWarningMessage ? (
      <div className="px-3 pt-3">
        <AiFormWarningAlert
          message={formWarningMessage}
          onClose={onCloseFormWarning ?? (() => undefined)}
        />
      </div>
    ) : null}
    <SimpleBarReact
      style={{
        height: isMdUp ? size.height - 180 : 'calc(85vh - 180px)',
        width: '100%'
      }}
    >
      <ChatItems messages={messages} keyword={keyword} />
    </SimpleBarReact>
  </Card.Body>
);

const AiModalContent = ({
  isMdUp,
  size,
  keyword,
  messages,
  isFetchingHistoryList,
  isFetchingHistoryDetails,
  hasKeywordHistory,
  isHistoryListLoaded,
  isFetchingAnalysis,
  suppressAnalysisLoading,
  showInsufficientBalance = false,
  showNoActiveApiKeyAlert = false,
  onCloseNoActiveApiKeyAlert,
  formWarningMessage = null,
  onCloseFormWarning,
  needsApiKeySetup = false,
  canUseAi = true
}: AiModalContentProps) => {
  const showChatPreload =
    isFetchingHistoryList ||
    !isHistoryListLoaded ||
    (hasKeywordHistory && isFetchingHistoryDetails);

  const showAnalysisLoading =
    !suppressAnalysisLoading &&
    !showChatPreload &&
    !showInsufficientBalance &&
    !showNoActiveApiKeyAlert &&
    !formWarningMessage &&
    !needsApiKeySetup &&
    messages.length === 0 &&
    ((!hasKeywordHistory && isHistoryListLoaded) || isFetchingAnalysis);

  if (showChatPreload) {
    return <ChatPreload isMdUp={isMdUp} size={size} />;
  }

  if (showInsufficientBalance && messages.length === 0) {
    return (
      <div className="flex-1 d-flex" style={{ minHeight: 0 }}>
        <AiModalAccessAlerts
          needsApiKeySetup={false}
          canUseAi
          showInsufficientBalance
        />
      </div>
    );
  }

  if (showNoActiveApiKeyAlert && messages.length === 0) {
    return (
      <div className="flex-1 d-flex flex-column p-3" style={{ minHeight: 0 }}>
        <AiNoActiveApiKeyAlert
          show={showNoActiveApiKeyAlert}
          onClose={onCloseNoActiveApiKeyAlert ?? (() => undefined)}
        />
      </div>
    );
  }

  if (formWarningMessage && messages.length === 0) {
    return (
      <div className="flex-1 d-flex flex-column p-3" style={{ minHeight: 0 }}>
        <AiFormWarningAlert
          message={formWarningMessage}
          onClose={onCloseFormWarning ?? (() => undefined)}
        />
      </div>
    );
  }

  if (needsApiKeySetup && messages.length === 0) {
    return (
      <div className="flex-1 d-flex" style={{ minHeight: 0 }}>
        <AiModalAccessAlerts needsApiKeySetup canUseAi={canUseAi} />
      </div>
    );
  }

  if (showAnalysisLoading) {
    return (
      <AnalysisLoadingState
        keyword={keyword}
        isMdUp={isMdUp}
        size={size}
      />
    );
  }

  return (
    <ChatScrollArea
      isMdUp={isMdUp}
      size={size}
      messages={messages}
      keyword={keyword}
      showInsufficientBalance={showInsufficientBalance}
      showNoActiveApiKeyAlert={showNoActiveApiKeyAlert}
      onCloseNoActiveApiKeyAlert={onCloseNoActiveApiKeyAlert}
      formWarningMessage={formWarningMessage}
      onCloseFormWarning={onCloseFormWarning}
    />
  );
};

export default AiModalContent;
